import React, {useEffect, useState} from "react";
import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	FormControlLabel,
	InputAdornment,
	Pagination,
	Skeleton,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
	InterviewList,
	InterviewRestControllerApiFactory,
	PageDtoInterviewList,
	PersonRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useNavigate, useParams} from "react-router-dom";
import dayjs from "dayjs";
import AdminInner from "../../../../components/layout/admin_inner";
import {useApiManager} from "../../../../components/utils/api";
import {parsePathId} from "../../../../components/form/form_utils";
import {dateToString} from "../../../../components/utils/dates";
import {INTERVIEW_EXIT_RISK_LABELS, INTERVIEW_HEALTH_LABELS} from "../../../../components/form/types";
import {avatarColor, btnSx, fullName, initials, panelSx, relative, Signal} from "./ui";

const PAGE_SIZE = 25;

/** Los años con entrevistas: desde la primera registrada (2022) hasta el actual. */
const YEARS = (() => {
	const years: number[] = [];
	for (let y = dayjs().year(); y >= 2022; y--) {
		years.push(y);
	}
	return years;
})();

/** Lo que se anotó, en una línea: oportunidades primero, si no el motivo o los comentarios. */
const notes = (i: InterviewList) => i.opportunities || i.healthReason || i.privateComments || "";

const PersonCell = ({i}: { i: InterviewList }) => {
	const name = fullName(i.person);
	return (
		<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
			<Avatar sx={{width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(name)}}>{initials(name)}</Avatar>
			<Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
		</Stack>
	);
};

const DateCell = ({i}: { i: InterviewList }) => (
	<Box>
		<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(i.interviewDate)}</Typography>
		<Typography variant="caption" color="text.secondary">{relative(i.interviewDate)}</Typography>
	</Box>
);

/**
 * Listado de entrevistas (#13808): todas, o las de una persona si viene en la ruta. Búsqueda por
 * nombre o NIF, filtro por año y eliminadas aparte; de la más reciente a la más antigua.
 */
export const InterviewListPage = () => {
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const params = useParams();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const personId = parsePathId(params, "personId");
	const ctrl = apiManager.factory(InterviewRestControllerApiFactory);

	const [page, setPage] = useState(1);
	const [term, setTerm] = useState("");
	const [query, setQuery] = useState("");
	const [year, setYear] = useState<number>();
	const [showDismissed, setShowDismissed] = useState(false);
	const [result, setResult] = useState<PageDtoInterviewList>();
	const [personName, setPersonName] = useState<string>();

	// La búsqueda espera a que se deje de escribir.
	useEffect(() => {
		const t = setTimeout(() => {
			setQuery(term.trim());
			setPage(1);
		}, 300);
		return () => clearTimeout(t);
	}, [term]);

	useEffect(() => {
		setResult(undefined);
		apiManager.execute(ctrl.pageInterview(page - 1, PAGE_SIZE, undefined, personId, year, undefined, undefined,
			query || undefined, showDismissed || undefined))
			.then(setResult)
			.catch(() => setResult({content: [], totalElements: 0, totalPages: 0}));
	}, [page, query, year, showDismissed, personId]);

	useEffect(() => {
		if (personId) {
			apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(personId))
				.then(s => setPersonName(fullName(s.person))).catch(() => undefined);
		}
	}, [personId]);

	const base = personId ? "/app/admin/employee/person/" + personId + "/interview" : "/app/admin/employee/interview";
	const open = (i: InterviewList) => navigate(base + "/" + i.id);
	const rows = result?.content || [];
	const total = result?.totalElements ?? 0;

	return (
		<AdminInner withoutPaper>
			<Box sx={{maxWidth: 1180}}>
				<Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="space-between" alignItems={{sm: "flex-end"}} sx={{mb: 3}}>
					<Box sx={{minWidth: 0}}>
						{personId && (
							<Button size="small" startIcon={<ArrowBackRoundedIcon/>} onClick={() => navigate(-1)}
									sx={{...btnSx, ml: -1, mb: 0.5, color: "text.secondary"}}>
								Volver
							</Button>
						)}
						<Typography variant="h5" component="h1" fontWeight={700}>
							{personId ? `Entrevistas de ${personName || "…"}` : "Entrevistas"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{result ? `${total} ${total === 1 ? "entrevista" : "entrevistas"}${year ? " en " + year : ""}` : " "}
						</Typography>
					</Box>
					<Button variant="contained" disableElevation startIcon={<AddRoundedIcon/>} onClick={() => navigate(base + "/new")}
							sx={{...btnSx, px: 2.5, alignSelf: {xs: "flex-start", sm: "auto"}}}>
						Nueva entrevista
					</Button>
				</Stack>

				<Card elevation={0} sx={panelSx}>
					<Stack direction={{xs: "column", md: "row"}} spacing={1.5} justifyContent="space-between" alignItems={{md: "center"}} sx={{mb: 2}}>
						<Stack direction="row" spacing={1} sx={{overflowX: "auto", pb: {xs: 0.5, md: 0}}}>
							<Chip label="Todos los años" clickable onClick={() => {
								setYear(undefined);
								setPage(1);
							}} color={!year ? "primary" : "default"} variant={!year ? "filled" : "outlined"} sx={{fontWeight: 600, flexShrink: 0}}/>
							{YEARS.map(y => (
								<Chip key={y} label={y} clickable onClick={() => {
									setYear(y);
									setPage(1);
								}} color={year === y ? "primary" : "default"} variant={year === y ? "filled" : "outlined"}
									  sx={{fontWeight: 600, flexShrink: 0}}/>
							))}
						</Stack>
						<Stack direction="row" spacing={2} alignItems="center">
							<FormControlLabel sx={{mr: 0, whiteSpace: "nowrap"}}
											  control={<Switch size="small" checked={showDismissed} onChange={e => {
												  setShowDismissed(e.target.checked);
												  setPage(1);
											  }}/>}
											  label={<Typography variant="body2">Eliminadas</Typography>}/>
							{!personId && (
								<TextField size="small" placeholder="Buscar persona o NIF" value={term} onChange={e => setTerm(e.target.value)}
										   sx={{width: {xs: "100%", md: 260}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}
										   InputProps={{startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small"/></InputAdornment>}}/>
							)}
						</Stack>
					</Stack>

					{!result ? (
						<Stack spacing={1}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={52}/>)}</Stack>
					) : rows.length === 0 ? (
						<Box sx={{py: 6, textAlign: "center"}}>
							<Typography variant="body2" color="text.secondary">
								{query || year ? "Ninguna entrevista con ese filtro." : "Aún no hay entrevistas."}
							</Typography>
						</Box>
					) : compact ? (
						<Stack spacing={1.5}>
							{rows.map(i => (
								<Box key={i.id} role="link" tabIndex={0} onClick={() => open(i)} onKeyDown={e => e.key === "Enter" && open(i)}
									 sx={{
										 p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", cursor: "pointer",
										 opacity: i.dismissed ? 0.55 : 1, "&:hover": {borderColor: "primary.main"},
									 }}>
									<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
										{personId ? <DateCell i={i}/> : <PersonCell i={i}/>}
										{!personId && <Typography variant="caption" color="text.secondary" sx={{whiteSpace: "nowrap"}}>{dateToString(i.interviewDate)}</Typography>}
									</Stack>
									{(i.health || i.exitRisk || i.interviewer) && (
										<Stack direction="row" spacing={2} sx={{mt: 1.5}}>
											{i.health && <Signal value={i.health} labels={INTERVIEW_HEALTH_LABELS}/>}
											{i.exitRisk && <Signal value={i.exitRisk} labels={INTERVIEW_EXIT_RISK_LABELS}/>}
											{i.interviewer && <Typography variant="body2" color="text.secondary" noWrap>con {fullName(i.interviewer)}</Typography>}
										</Stack>
									)}
									{notes(i) && (
										<Typography variant="body2" color="text.secondary" sx={{
											mt: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
										}}>{notes(i)}</Typography>
									)}
								</Box>
							))}
						</Stack>
					) : (
						<TableContainer sx={{border: "1px solid", borderColor: "divider", borderRadius: 2.5}}>
							<Table size="small" sx={{
								tableLayout: "fixed",
								"& th": {bgcolor: "grey.50", color: "text.secondary", fontSize: 12, fontWeight: 700, py: 1.25, whiteSpace: "nowrap"},
								"& td": {py: 1.25, borderColor: "divider"},
								"& tbody tr:last-of-type td": {borderBottom: 0},
							}}>
								<TableHead>
									<TableRow>
										<TableCell sx={{width: 130}}>Fecha</TableCell>
										{!personId && <TableCell sx={{width: 250}}>Persona</TableCell>}
										<TableCell sx={{width: 190}}>Entrevistador</TableCell>
										<TableCell sx={{width: 100}}>Salud</TableCell>
										<TableCell sx={{width: 100}}>Riesgo</TableCell>
										<TableCell>Notas</TableCell>
										<TableCell sx={{width: 44}}/>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map(i => (
										<TableRow key={i.id} hover onClick={() => open(i)}
												  sx={{cursor: "pointer", opacity: i.dismissed ? 0.5 : 1}}>
											<TableCell><DateCell i={i}/></TableCell>
											{!personId && <TableCell><PersonCell i={i}/></TableCell>}
											<TableCell>
												<Typography variant="body2" color={i.interviewer ? "text.primary" : "text.disabled"} noWrap>
													{i.interviewer ? fullName(i.interviewer) : "—"}
												</Typography>
											</TableCell>
											<TableCell><Signal value={i.health} labels={INTERVIEW_HEALTH_LABELS}/></TableCell>
											<TableCell><Signal value={i.exitRisk} labels={INTERVIEW_EXIT_RISK_LABELS}/></TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary" noWrap title={notes(i)}>
													{i.dismissed && <Chip size="small" label="Eliminada" sx={{mr: 1, height: 20}}/>}
													{notes(i) || "—"}
												</Typography>
											</TableCell>
											<TableCell align="right" sx={{color: "text.disabled"}}><ChevronRightRoundedIcon fontSize="small"/></TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					{(result?.totalPages || 0) > 1 && (
						<Stack alignItems="center" sx={{mt: 3}}>
							<Pagination count={result?.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded"
										size={compact ? "small" : "medium"}/>
						</Stack>
					)}
				</Card>
			</Box>
		</AdminInner>
	);
};
